
import { defineComponent, reactive, ref, toRefs } from 'vue'
import { useStore } from '@/store'
import { UserActionTypes } from '@/store/modules/user/action-types'
import { LoginInfoType } from '@/types/user'
import { ZoneCode } from './zoneCode'
import { ElMessage } from 'element-plus/lib/components'
import { UserMutationTypes } from '@/store/modules/user/mutation-types'
import { isValidPhone } from '@/utils/validate'
import useCountDown from '@/composable/useCountDown'
import { useRouter } from 'vue-router'
import ForgotPass from './forgot-pass.vue'

interface ILoginState {
  loading: boolean
  btnDisabled: boolean
  activeName: string
  isKeepLogin: boolean
  passwordForm: {
    account: string
    password: string
  }
  nonPasswordForm: {
    account: string
    verificationCode: string
  }
  passwordRules: object
  nonPasswordRules: object
  identifyBtnText: string
  areaCodeData: {
    label: string
    value: number
  }[]
  areaCode: number | string
  isForgotPass: boolean
}
export default defineComponent({
  name: 'LoginDialog',
  components: {
    ForgotPass
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const store = useStore()
    /** 路由引入 */
    const router = useRouter()

    const passwordFormRef = ref()
    const nonPasswordFormRef = ref()

    const state = reactive<ILoginState>({
      loading: false,
      btnDisabled: false,
      activeName: 'password-login',
      isKeepLogin: store.state.user.user.isKeepLogin,
      passwordForm: {
        account: '',
        password: ''
      },
      nonPasswordForm: {
        account: '',
        verificationCode: ''
      },
      identifyBtnText: '获取验证码',
      areaCodeData: ZoneCode,
      areaCode: ZoneCode[0].value,
      isForgotPass: false,
      passwordRules: {
        account: [
          {
            required: true,
            pattern: /^[A-Za-z0-9]{6,20}$/,
            message: '请输入6-20位数字或英文',
            trigger: 'change'
          }
        ],
        password: [
          {
            required: true,
            pattern: /^.{6,20}$/,
            message: '请输入6-20位密码',
            trigger: 'change'
          },
          { min: 6, max: 20, message: '长度6-20个字符', trigger: 'blur' }
        ]
      },
      nonPasswordRules: {
        account: [
          {
            required: true,
            pattern: /^\d{5,20}$/,
            message: '请核对手机号是否正确',
            trigger: 'blur'
          }
        ],
        verificationCode: [
          {
            required: true,
            pattern: /^\d{6,6}$$/,
            message: '验证码格式不通过',
            trigger: 'blur'
          }
        ]
      }
    })
    const handleClose = () => {
      emit('close')
    }
    const [codeCountDown_Num, codeCountDown_IsRunning, codeCountDown_Start] =
      useCountDown(60)

    /** 点击获取验证码 */
    const handleSendIdentifyCode = () => {
      if (!isValidPhone(state.nonPasswordForm.account)) {
        ElMessage.warning('请核对手机号码是否正确！！！')
        return
      }
      codeCountDown_IsRunning.value = true
      getIdentifyCode()
    }
    /** 发送验证码请求 */
    const getIdentifyCode = () => {
      const params = {
        phone: state.nonPasswordForm.account,
        type: 2, // 0通用，1注册， 2登录，3修改密码
        areaCode: state.areaCode, // 手机区号 （国内可不填）
        pf: 3,
        version: 'web',
        evkAppId: '10009'
      }
      store
        .dispatch(UserActionTypes.ACTION_GET_IDENTIFY_CODE, params)
        .then((res: any) => {
          const errInfo = res.data.errInfo
          if (res.status === 1) {
            ElMessage.success('验证码发送成功')
            codeCountDown_Start()
          } else if (errInfo) {
            ElMessage.error(errInfo)
            setTimeout(() => {
              state.identifyBtnText = '重新获取'
              codeCountDown_IsRunning.value = true
            }, 2000)
          }
        })
        .catch((err) => {
          setTimeout(() => {
            state.identifyBtnText = '重新获取'
            codeCountDown_IsRunning.value = true
          }, 2000)
          console.error(err)
        })
    }
    /** 登录 */
    function handleLogin(activeName: string) {
      if (activeName === 'password-login') {
        passwordLogin()
      } else if (activeName === 'non-password-login') {
        nonPasswordLogin()
      }
      store.commit(UserMutationTypes.SET_IS_KEEP_LOGIN, state.isKeepLogin)
    }
    /** 密码登录 */
    const passwordLogin = () => {
      passwordFormRef.value.validate((valid: boolean) => {
        if (valid) {
          state.btnDisabled = true
          state.loading = true
          const params: LoginInfoType = {
            pf: 3,
            version: 'web',
            evkAppId: '10009',
            phone: state.passwordForm.account,
            pwd: state.passwordForm.password,
            areaCode: state.areaCode,
            tokenValidityType: 3
          }
          store
            .dispatch(UserActionTypes.ACTION_LOGIN, params)
            .then((res: any) => {
              if (res) {
                state.loading = false
                state.btnDisabled = false
                ElMessage.success('登录成功')
                store.commit(UserMutationTypes.SET_SHOW_LOGIN_DIALOG, false)
              }
            })
            .catch((error: any) => {
              handleLoginFailure(error)
            })
        }
      })
    }
    /** 免密码登录 */
    const nonPasswordLogin = () => {
      nonPasswordFormRef.value.validate((valid: boolean) => {
        if (valid) {
          state.btnDisabled = true
          state.loading = true
          const params = {
            pf: 3, // 登录平台 1:android 2:ios 3:web 4:pc
            version: 'web', // 客户端版本号
            evkAppId: '10009', // 业务ID
            phone: state.nonPasswordForm.account,
            verificationCode: state.nonPasswordForm.verificationCode, // 验证码
            areaCode: state.areaCode, // 手机区号 （国内可不填）
            tokenValidityType: 3 // token过期策略
          }
          store
            .dispatch(UserActionTypes.ACTION_VALIDATE_IDENTIFY_CODE, params)
            .then((res: any) => {
              if (res) {
                state.loading = false
                state.btnDisabled = false
                codeCountDown_IsRunning.value = false
                ElMessage.success('登录成功')
                store.commit(UserMutationTypes.SET_SHOW_LOGIN_DIALOG, false)
              }
            })
            .catch((error: any) => {
              handleLoginFailure(error)
            })
        }
      })
    }

    const handleLoginFailure = (error: any) => {
      setTimeout(() => {
        state.loading = false
        state.btnDisabled = false
      }, 2000)
      let info =
        error.errInfo && error.errInfo.message
          ? error.errInfo.message
          : '服务器错误'
      ElMessage.error(info)
    }
    /** 跳转服务协议 */
    const openUserServe = () => {
      window.open('/userServe', ' _blank')
    }

    /** 忘记密码 */
    const handleForgetPass = () => {
      state.isForgotPass = !state.isForgotPass
    }

    /** 密码更换成功回调 */
    const handleUpdatePwdSucc = () => {
      state.isForgotPass = false
    }

    return {
      codeCountDown_Num,
      codeCountDown_IsRunning,
      passwordFormRef,
      nonPasswordFormRef,
      ...toRefs(state),
      handleClose,
      handleSendIdentifyCode,
      handleLogin,
      openUserServe,
      handleForgetPass,
      handleUpdatePwdSucc
    }
  }
})
